import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border bg-background text-foreground hover:bg-muted aria-expanded:bg-muted dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default:
          'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        // VOLL: degrau de toque do app — 44px, Apple HIG. Só pra
        // controles que o polegar aperta; não é degrau de "importância".
        xl: 'h-11 gap-2 px-4 text-[0.9375rem] has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
        'icon-xl': 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * **Botão só-ícone exige nome acessível.** Nos tamanhos `icon*` não há texto visível, e sem nome o
 * leitor de tela anuncia só "botão" — é a violação `button-name` (crítica, WCAG 2.1 AA 4.1.2) que a
 * varredura de 2026-07-31 pegou na galeria deste átomo. Vale QUALQUER uma das três formas (axe
 * aceita as três; ver a story `ComIconeAcessivel`):
 *
 * 1. `aria-label="Criar"` no próprio botão — o caminho normal.
 * 2. `<span className="sr-only">Criar</span>` como filho, junto do glifo — o nome vem do conteúdo,
 *    que continua legível pro leitor de tela porque `sr-only` recorta, não esconde (`display:none`
 *    mataria o nome).
 * 3. Em composição `render={<Button size="icon" />}`, o nome pode vir do WRAPPER (é assim que os
 *    "X" de `Dialog`/`Sheet` e o kebab do `DataTable` já se resolvem, via `sr-only` no wrapper).
 *
 * Não está travado no TIPO de propósito: as formas 2 e 3 vivem em `children`/no componente de fora,
 * e o TypeScript não enxerga nem um nem outro. Exigir `aria-label` quando `size` é `icon*` daria
 * erro em 15 usos hoje CORRETOS (medido no repo) — os `render={<Button />}` de Dialog, Sheet,
 * DropdownMenuTrigger e TooltipTrigger. O guarda de verdade é o addon de a11y do Storybook
 * (`parameters.a11y.test = 'error'` em `design/.storybook/preview.tsx`), que roda o axe e reprova.
 */
function Button({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
